import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BuildIcon from "@mui/icons-material/Build";
import { MaintenanceItemType, IAircraft, IMaintenanceItem, MaintenanceCounterType } from "@vmosolutions/oc-interfaces";
import { getCategorisedMaintenanceItems } from "./AircraftMaintenanceUtils";
import { CalendarMonth } from "@mui/icons-material";
import moment from "moment-timezone";

interface IAircraftMaintenanceTabProps {
  aircraft: IAircraft | null;
  maintenanceEnabled: boolean;
}

/**
 * Basic styling for the different maintenance item types' lists.
 */
interface IMaintenanceItemTypeStyle {
  label: string;
  color: "error" | "warning" | "info" | "success";
  icon: JSX.Element;
}

const overdueStyle: IMaintenanceItemTypeStyle = {
  label: "Overdue",
  color: "error",
  icon: <ErrorOutlineIcon fontSize="small" />,
};

const counterStyle: IMaintenanceItemTypeStyle = {
  label: "Next Due",
  color: "info",
  icon: <ScheduleIcon fontSize="small" />,
};

const inspectionStyle: IMaintenanceItemTypeStyle = {
  label: "Next Due",
  color: "info",
  icon: <CalendarMonth fontSize="small" />,
};

const deferredStyle: IMaintenanceItemTypeStyle = {
  label: "Deferred",
  color: "warning",
  icon: <WarningAmberIcon fontSize="small" />,
};

const completedStyle: IMaintenanceItemTypeStyle = {
  label: "Completed",
  color: "success",
  icon: <CheckCircleOutlineIcon fontSize="small" />,
};

const defaultStyleMap: Record<MaintenanceItemType, IMaintenanceItemTypeStyle> = {
  [MaintenanceItemType.Counter]: counterStyle,
  [MaintenanceItemType.Deferred]: deferredStyle,
  [MaintenanceItemType.Inspection]: inspectionStyle,
  [MaintenanceItemType.Complete]: completedStyle,
};

const formatDate = (date: Date | string | undefined | null): string => {
  if (!date) return "N/A";
  const d = new Date(date);
  return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString();
};

/** Tighter line-height for wrapped lines. */
const captionSx = { lineHeight: 1.2 };

/** Single caption line used in secondary content. */
const Caption = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <Typography variant="caption" color="text.secondary" display="inline-block" sx={captionSx}>
    {children}
  </Typography>
);

/** Renders type-specific secondary details for a maintenance item row. */
const getItemSecondaryContent = (item: IMaintenanceItem): JSX.Element => {
  const detail = (() => {
    switch (item.itemType) {
      case MaintenanceItemType.Counter:
        return item.counters.map((counter) => (
          <Caption key={counter.name ?? counter.type}>
            {counter.name ?? counter.type}
            <b>
              {counter.dueAtDate && ` · Due: ${formatDate(counter.dueAtDate)} (${moment(counter.dueAtDate).fromNow()})`}
            </b>
            <b>{counter.dueAtValue != null && ` · Due at: ${counter.dueAtValue}`}</b>
            <b>
              {counter.dueAtValue != null &&
                counter.type !== MaintenanceCounterType.Other &&
                ` ${counter.type.toLowerCase()}`}
            </b>
            <i>{counter.description && ` · ${counter.description}`}</i>
          </Caption>
        ));
      case MaintenanceItemType.Deferred:
        return (
          <Caption>
            MEL: {item.mel.reference}
            {item.mel.category && ` · Category: ${item.mel.category}`}
            <b>{` · Due: ${formatDate(item.mel.dueDate)}`}</b>
            <b>{item.mel.dueDate && ` (${moment(item.mel.dueDate).fromNow()})`}</b>
          </Caption>
        );
      case MaintenanceItemType.Inspection:
        return (
          <Caption>
            <b>Projected Due Date: {formatDate(item.projectedDueDate)}</b>
            <b>{item.projectedDueDate && ` (${moment(item.projectedDueDate).fromNow()})`}</b>
          </Caption>
        );
      case MaintenanceItemType.Complete:
        return (
          <Caption>
            <b>Completed: {formatDate(item.completedDate)}</b>
            <b>{item.completedDate && ` (${moment(item.completedDate).fromNow()})`}</b>
          </Caption>
        );
    }
  })();

  return (
    <Box display="flex" flexDirection="column" gap={0.3}>
      {item.description && <Caption>{item.description}</Caption>}
      {detail}
    </Box>
  );
};

// Components

const MaintenanceItemRow = ({
  item,
  style,
}: {
  item: IMaintenanceItem;
  style: IMaintenanceItemTypeStyle;
}): JSX.Element => {
  const theme = useTheme();

  const itemIcon = defaultStyleMap[item.itemType]?.icon ?? style.icon;

  return (
    <ListItem
      sx={{
        py: 0.5,
        px: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <ListItemIcon sx={{ minWidth: 32, color: `${style.color}.main` }}>{itemIcon}</ListItemIcon>
      <ListItemText
        secondaryTypographyProps={{ component: "div" }}
        primary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {item.itemId}
            </Typography>
            {item.component && <Typography variant="body2">{item.component}</Typography>}
          </Box>
        }
        secondary={getItemSecondaryContent(item)}
      />
    </ListItem>
  );
};

const MaintenanceSection = ({
  items,
  style,
  defaultExpanded = false,
}: {
  items: IMaintenanceItem[];
  style: IMaintenanceItemTypeStyle;
  defaultExpanded?: boolean;
}): JSX.Element | null => {
  if (items.length === 0) return null;

  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ "&::before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: `${style.color}.main` }}>
          {style.icon}
          <Typography variant="subtitle2" color="inherit">
            {style.label} ({items.length})
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <List dense disablePadding>
          {items.map((item) => (
            <MaintenanceItemRow key={item.itemId} item={item} style={style} />
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};

export const AircraftMaintenanceTab = ({ aircraft, maintenanceEnabled }: IAircraftMaintenanceTabProps): JSX.Element => {
  const theme = useTheme();

  if (!aircraft) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No aircraft selected.
        </Typography>
      </Box>
    );
  }

  const maintenance = aircraft.maintenance;
  const { overdue, nextDue, deferred, completed } = getCategorisedMaintenanceItems(maintenance);

  return (
    <Box sx={{ overflow: "auto", height: "100%" }}>
      {/* Maintenance Summary */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <BuildIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Maintenance Summary
          </Typography>
        </Box>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Chip
            size="small"
            label={`Overdue: ${overdue.length}`}
            color={overdueStyle.color}
            variant={overdue.length > 0 ? "filled" : "outlined"}
          />
          <Chip
            size="small"
            label={`Next Due: ${nextDue.length}`}
            color={counterStyle.color}
            variant={nextDue.length > 0 ? "filled" : "outlined"}
          />
          <Chip
            size="small"
            label={`Deferred: ${deferred.length}`}
            color={deferredStyle.color}
            variant={deferred.length > 0 ? "filled" : "outlined"}
          />
          <Chip size="small" label={`Completed: ${completed.length}`} color={completedStyle.color} variant="outlined" />
        </Box>
      </Box>

      <MaintenanceSection items={overdue} style={overdueStyle} defaultExpanded />
      <MaintenanceSection items={nextDue} style={counterStyle} defaultExpanded />
      <MaintenanceSection items={deferred} style={deferredStyle} />
      <MaintenanceSection items={completed} style={completedStyle} />

      {/* Sync Info */}
      <Box sx={{ px: 2, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        {maintenanceEnabled ? (
          maintenance ? (
            // Maintenance enabled and aircraft has maintenance data.
            <Typography variant="caption" color="text.secondary">
              ⓘ{" "}
              <i>
                Last synced: {formatDate(maintenance.lastSyncedAt)} · Source: {maintenance.syncSource}
              </i>
            </Typography>
          ) : (
            // Maintenance enabled but no maintenance data for this aircraft.
            <Typography variant="caption" color="text.secondary">
              ⓘ <i>This aircraft has no maintenance data.</i>
            </Typography>
          )
        ) : (
          // Maintenance integration not enabled.
          <Typography variant="caption" color="text.secondary">
            ⓘ <i>Maintenance integration feature unavailable.</i>
          </Typography>
        )}
      </Box>
    </Box>
  );
};
